import { Button, Modal, TreeSelect } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { classOptionsAction } from 'redux/action/classAction';
import { AppDispatch } from 'redux/store';
import { getApi, postApi } from 'redux/apis';
import { checkPermission, toastText } from 'utils/utils';
import styles from './index.module.scss';

interface ClassReferenceIssue {
	employeeId: string;
	classId: string;
	cachedClassName: string | null;
	currentClassName: string | null;
	// 'renamed' never reaches here - the backend heals that case silently and
	// reports it as 'ok'. Only genuinely unresolvable references (merged
	// away/deactivated in QBO, no safe auto-remap possible) come back
	// 'missing' and need a human to pick a replacement.
	status: 'ok' | 'missing';
}

interface StaleClassGroup {
	classId: string;
	cachedClassName: string | null;
	employeeCount: number;
}

interface Props {
	open: boolean;
	onClose: () => void;
	companyId: string;
	payPeriodId: string;
	onResolved?: () => void;
}

// CAP-94: standalone, reusable resolve UI for stale QBO class references —
// mounted from the Cost Allocations page (proactive banner) and from the
// journal Publish flow (reactive, when the publish-time guard blocks it).
// Groups affected employees by the stale class so an admin fixes each class
// once instead of reselecting it on every employee.
export const ResolveClassReferencesModal = (props: Props) => {
	const { open, onClose, companyId, payPeriodId, onResolved } = props;
	const dispatch = useDispatch<AppDispatch>();

	const { optionData: classOptions } = useSelector((state: any) => state.class);

	const selectedCompanyPermission = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
	);
	const isEditEmployeeCostPermission = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Employee Cost',
			permission: ['edit'],
		}
	);

	const [isLoading, setIsLoading] = useState(false);
	const [staleGroups, setStaleGroups] = useState<StaleClassGroup[]>([]);
	const [selectedReplacement, setSelectedReplacement] = useState<
		Record<string, string>
	>({});
	const [applyingClassId, setApplyingClassId] = useState<string | null>(null);

	const loadStatus = async () => {
		if (!companyId || !payPeriodId) {
			return;
		}

		setIsLoading(true);
		try {
			const res = await getApi('/employee-config/class-reference-status', {
				companyId,
				payPeriodId,
			});
			const issues: ClassReferenceIssue[] = res?.data?.data || [];
			const groups = new Map<string, StaleClassGroup>();

			issues
				.filter((issue) => issue.status === 'missing')
				.forEach((issue) => {
					const existing = groups.get(issue.classId);
					if (existing) {
						existing.employeeCount += 1;
					} else {
						groups.set(issue.classId, {
							classId: issue.classId,
							cachedClassName: issue.cachedClassName,
							employeeCount: 1,
						});
					}
				});

			setStaleGroups(Array.from(groups.values()));
		} catch (error: any) {
			toastText(
				error?.response?.data?.message ||
					'Error loading class reference status',
				'error'
			);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
			dispatch(classOptionsAction());
			loadStatus();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, companyId, payPeriodId]);

	const handleApply = async (group: StaleClassGroup) => {
		const newClassId = selectedReplacement[group.classId];

		if (!newClassId) {
			toastText('Please select a replacement class', 'error');
			return;
		}

		setApplyingClassId(group.classId);
		try {
			const res = await postApi('/employee-config/reassign-class', {
				companyId,
				payPeriodId,
				oldClassId: group.classId,
				newClassId,
			});

			toastText(
				`Updated ${res?.data?.data?.updatedCount || group.employeeCount} employee(s) to "${res?.data?.data?.newClassName}"`,
				'success'
			);

			await loadStatus();
			onResolved?.();
		} catch (error: any) {
			toastText(
				error?.response?.data?.message || 'Error updating class reference',
				'error'
			);
		} finally {
			setApplyingClassId(null);
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			closable
			footer={null}
			width={700}
			title={
				<span>
					<WarningOutlined style={{ color: '#faad14' }} /> &nbsp; Resolve
					Outdated Class References
				</span>
			}
		>
			<div className={styles['resolve-modal']}>
				<p style={{ fontSize: '12px', color: '#818181', marginBottom: '1rem' }}>
					These QuickBooks classes are no longer valid (merged or deactivated
					in QuickBooks) since they were selected on an employee&apos;s cost
					allocation. Pick a replacement for each one below — it will update
					every affected employee at once.
				</p>

				{isLoading ? (
					<div className={styles['empty-state']}>Loading...</div>
				) : staleGroups.length === 0 ? (
					<div className={styles['empty-state']}>
						No outdated class references found for this pay period.
					</div>
				) : !isEditEmployeeCostPermission ? (
					<div className={styles['empty-state']}>
						You don&apos;t have permission to resolve this. Contact someone
						with Employee Cost edit access.
					</div>
				) : (
					staleGroups.map((group) => (
						<div className={styles['row']} key={group.classId}>
							<div className={styles['row-info']}>
								<div className={styles['row-old-name']}>
									{group.cachedClassName || group.classId}
								</div>
								<div className={styles['row-detail']}>
									{`No longer a valid class in QuickBooks — used by ${group.employeeCount} employee(s)`}
								</div>
							</div>
							<div className={styles['row-action']}>
								<TreeSelect
									style={{ width: 240 }}
									placeholder="Select replacement class"
									treeData={classOptions}
									showSearch
									treeDefaultExpandAll
									value={selectedReplacement[group.classId]}
									onChange={(value: string) =>
										setSelectedReplacement((prev) => ({
											...prev,
											[group.classId]: value,
										}))
									}
									filterTreeNode={(inputValue, treeNode) =>
										treeNode.props.title
											?.toLowerCase()
											.includes(inputValue.toLowerCase())
									}
								/>
								<Button
									className={styles['apply']}
									disabled={applyingClassId === group.classId}
									onClick={() => handleApply(group)}
								>
									{applyingClassId === group.classId ? 'Applying...' : 'Apply'}
								</Button>
							</div>
						</div>
					))
				)}

				<div
					style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}
				>
					<Button className={styles['close']} onClick={onClose}>
						Close
					</Button>
				</div>
			</div>
		</Modal>
	);
};
